import { DashedTag } from '@praetorian-chariot/ui';
import { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import { useAssetTags } from '@/hooks/useAssetTags';
import { useAxios } from '@/hooks/useAxios';
import { Asset, Risk, Seed, Technology } from '@/types';

/**
 * RemoveTag Modal Component
 *
 * Reusable UI component for removing tags from entities (assets, technologies, etc.).
 * Displays common tags across all selected entities and allows user to select
 * ONE tag for removal.
 *
 * Features:
 * - Auto-fetches common tags across selected entities
 * - Single-tag selection interface (radio-button style)
 * - Visual feedback showing selected tag
 * - Loading states while fetching
 * - Graceful handling of no common tags
 * - Generic support for different entity types (asset, technology, etc.)
 *
 * User Experience:
 * - Click a tag to select it (tag becomes highlighted)
 * - Clicking another tag replaces the previous selection (only one tag at a time)
 * - Button remains disabled until a tag is selected
 * - Tags are removed immediately when form is submitted
 *
 * @example
 * ```tsx
 * <RemoveTag
 *   open={open}
 *   onClose={onClose}
 *   disabled={false}
 *   onSubmit={(tag) => handleRemoveTag(tag)}
 *   assetKeys={['#asset#key1', '#asset#key2']}
 *   label="asset"  // or "technology"
 * />
 * ```
 */
export const RemoveTag = ({
  open,
  onClose,
  disabled,
  onSubmit,
  assetKeys,
  label = 'asset',
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  onSubmit: (tag: string) => void;
  assetKeys: string[];
  label?: 'asset' | 'technology' | 'seed' | 'vulnerability';
}) => {
  const axios = useAxios();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // ✅ NEW: Use useAssetTags hook for single asset case
  const singleAssetKey =
    label === 'asset' && assetKeys.length === 1 ? assetKeys[0] : undefined;
  const {
    tags: hookTags,
    isLoading: hookLoading,
    error: hookError,
  } = useAssetTags(singleAssetKey);

  // Keep existing pattern for multiple assets or non-asset entity types
  const [manualTags, setManualTags] = useState<string[]>([]);
  const [manualLoading, setManualLoading] = useState(false);

  // Use hook data for single asset, manual fetch for multiple/non-asset
  const usingSingleAssetHook = !!singleAssetKey;
  const commonTags = usingSingleAssetHook ? hookTags : manualTags;
  const loading = usingSingleAssetHook ? hookLoading : manualLoading;

  // Fetch tags for multiple assets or non-asset entities (existing pattern)
  useEffect(() => {
    // Skip if using single asset hook
    if (usingSingleAssetHook) {
      return;
    }

    if (!open || assetKeys.length === 0) {
      // Reset state when modal closes
      setManualTags([]);
      setSelectedTag(null);
      return;
    }

    const fetchCommonTags = async () => {
      setManualLoading(true);
      try {
        // Fetch all entities in parallel
        const promises = assetKeys.map(key =>
          axios.get(`/my`, {
            params: {
              label: label,
              key: key,
              exact: true,
            },
          })
        );
        const responses = await Promise.all(promises);

        // Extract tags from each entity with proper type handling
        // Response structure: { assets: [...] } or { technologies: [...] } or { seeds: [...] } or { risks: [...] }
        const allTags = responses.map(res => {
          if (label === 'asset') {
            return (res.data.assets?.[0] as Asset)?.tags || [];
          } else if (label === 'technology') {
            return (res.data.technologies?.[0] as Technology)?.tags || [];
          } else if (label === 'vulnerability') {
            return (res.data.risks?.[0] as Risk)?.tags || [];
          } else {
            return (res.data.seeds?.[0] as Seed)?.tags || [];
          }
        });

        // Collect all unique tags across all selected entities
        const uniqueTags = Array.from(new Set(allTags.flat())).sort();

        setManualTags(uniqueTags);
        setSelectedTag(null); // Start with nothing selected
      } catch (error) {
        console.error('[RemoveTag] Failed to fetch tags:', error);
        setManualTags([]);
      } finally {
        setManualLoading(false);
      }
    };

    fetchCommonTags();
  }, [open, assetKeys, axios, label, usingSingleAssetHook]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedTag(null);
    }
  }, [open]);

  function reset() {
    setSelectedTag(null);
    onClose();
  }

  // Handle case where no tags exist on any entity
  const entityName =
    label === 'asset'
      ? 'assets'
      : label === 'seed'
        ? 'seeds'
        : label === 'vulnerability'
          ? 'vulnerabilities'
          : 'technologies';
  if (commonTags.length === 0 && !loading) {
    return (
      <Modal
        title="Remove Tag"
        open={open}
        onClose={onClose}
        footer={{
          text: 'Close',
          onClick: onClose,
        }}
      >
        <p className="text-chariot-text-secondary">
          No tags found on selected {entityName}.
        </p>
        <p className="text-chariot-text-secondary mt-2 text-sm">
          Add tags to {entityName} before attempting to remove them.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Remove Tag"
      open={open}
      onClose={onClose}
      footer={{
        text: 'Remove Tag',
        form: 'remove-tag-form',
        disabled: disabled || !selectedTag || loading,
      }}
    >
      <p className="text-chariot-primary mb-4">
        Select a tag to remove from selected {entityName}:
      </p>
      {selectedTag && (
        <p className="text-chariot-text-secondary mb-4 text-sm">
          Selected: <span className="font-semibold">{selectedTag}</span>
        </p>
      )}
      <p className="text-chariot-text-secondary mb-4 text-sm">
        The tag will only be removed from {entityName} that currently have it.
      </p>

      <form
        id="remove-tag-form"
        className="space-y-4"
        onSubmit={async event => {
          event.preventDefault();
          if (selectedTag) {
            await onSubmit(selectedTag);
            reset();
          }
        }}
      >
        {loading ? (
          <p className="text-chariot-text-secondary">Loading tags...</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {commonTags.map(tag => (
              <DashedTag
                key={tag}
                label={tag}
                size="sm"
                active={selectedTag === tag}
                onClick={() => {
                  // Radio-button behavior: clicking selected tag does nothing,
                  // clicking another tag selects it (only one at a time)
                  setSelectedTag(tag);
                }}
                className="cursor-pointer"
                data-testid={`tag-option-${tag}`}
              />
            ))}
          </div>
        )}
      </form>
    </Modal>
  );
};
